// Policy page content for Hireloop PRO / Encore Business Services Group, LLC.
// Each policy is a list of { heading, body } sections. body is a plain string
// where blank lines separate paragraphs and lines beginning with "- " become
// bullets. Keep wording plain-English and tailored to this product.

const COMPANY = "Encore Business Services Group, LLC";
const PRODUCT = "Hireloop PRO";
const EFFECTIVE = "February 1, 2026";

export const POLICIES = {
  cookies: {
    slug: "cookies",
    title: "Cookies Policy",
    intro: `This Cookies Policy explains how ${PRODUCT}, operated by ${COMPANY}, uses cookies and similar technologies on hireloop.pro. By using the site you agree to the placement of cookies as described below.`,
    sections: [
      {
        heading: "1. What are cookies?",
        body: `Cookies are small text files stored on your device by your browser. They let websites remember you between visits, keep you signed in, and measure how the site is used.`,
      },
      {
        heading: "2. Cookies we use",
        body: `- Essential — keep you signed in (the "aip_token" JWT) and route authenticated requests. The site does not function without these.
- Analytics — anonymised usage data via PostHog to understand which features get used.
- Stripe — required for secure checkout when you subscribe to a paid plan.

We do not run advertising cookies or sell data to third parties.`,
      },
      {
        heading: "3. Controlling cookies",
        body: `You can clear cookies at any time from your browser settings. Removing the "aip_token" cookie will sign you out. Disabling cookies entirely will prevent you from using the platform.`,
      },
      {
        heading: "4. Changes to this policy",
        body: `We may update this policy as the platform evolves. The effective date above will be revised accordingly. We encourage you to revisit this page periodically.`,
      },
      {
        heading: "5. Contact",
        body: `Questions about cookies? Email privacy@hireloop.ai.`,
      },
    ],
  },
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    intro: `${COMPANY} respects your privacy. This policy describes what we collect, why we collect it, and the choices you have about your data when using ${PRODUCT}.`,
    sections: [
      {
        heading: "1. Information we collect",
        body: `- Account data — your name, email, password (hashed with bcrypt), and company name.
- Job and interview data — job titles, questions, candidate responses (video, transcripts, AI scores), and uploaded résumés.
- Billing data — handled by Stripe; we store only the customer ID and the plan/expiry date, not card numbers.
- Usage telemetry — anonymised events, pages visited, feature interactions.`,
      },
      {
        heading: "2. How we use your data",
        body: `We use your data to operate the platform: generate AI interview questions, transcribe and score answers, send invite and reminder emails (via Resend), and provide pipeline analytics. We never sell personal data.`,
      },
      {
        heading: "3. Candidate data",
        body: `Candidates uploading a résumé or recording an interview consent to processing for the specific role they applied to. Employers are the data controllers for candidate records they ingest. ${COMPANY} acts as the data processor.`,
      },
      {
        heading: "4. AI processing",
        body: `Some answers are routed through large language models (OpenAI GPT-5.2, Anthropic Claude) for transcription and scoring. These providers process data under contractual safeguards and do not train their public models on your data.`,
      },
      {
        heading: "5. Data retention",
        body: `Account data is retained until you delete your account. Interview videos and résumés are retained as long as the parent job exists; deleting a job cascades to all linked candidate records. Audit logs are kept for 12 months.`,
      },
      {
        heading: "6. Your rights",
        body: `You may request a copy of your data, correction of inaccuracies, or deletion of your account at any time by emailing privacy@hireloop.ai. We respond within 30 days.`,
      },
      {
        heading: "7. Security",
        body: `All traffic is encrypted in transit (TLS). Passwords are hashed with bcrypt. JWT sessions automatically rotate on password change to revoke other devices.`,
      },
      {
        heading: "8. Contact",
        body: `For privacy concerns or data subject requests, email privacy@hireloop.ai.`,
      },
    ],
  },
  terms: {
    slug: "terms",
    title: "Terms and Conditions",
    intro: `These Terms govern your access to and use of ${PRODUCT} ("the Service"), provided by ${COMPANY}. By creating an account or using the Service, you agree to these Terms.`,
    sections: [
      {
        heading: "1. Eligibility",
        body: `You must be at least 18 years old and legally able to enter contracts to use ${PRODUCT}. By signing up you confirm the information you provide is accurate and current.`,
      },
      {
        heading: "2. Accounts",
        body: `You are responsible for safeguarding your password and for any activity on your account. Notify us immediately at security@hireloop.ai if you suspect unauthorised access.`,
      },
      {
        heading: "3. Acceptable use",
        body: `You agree not to:
- use the Service for unlawful hiring practices or discriminatory screening;
- attempt to reverse-engineer, scrape, or overload the platform;
- upload résumés or candidate data you do not have the right to process.`,
      },
      {
        heading: "4. Subscriptions and billing",
        body: `Paid plans (Starter, Pro, Enterprise) are billed monthly in advance via Stripe. New accounts receive a 10-day free trial of Pro; you can cancel before the trial ends with no charge. Refunds are at our discretion.`,
      },
      {
        heading: "5. AI outputs",
        body: `AI-generated scores, summaries, and feedback are advisory only. ${COMPANY} does not guarantee accuracy and is not liable for hiring decisions made based on AI output. You are solely responsible for compliance with employment law in your jurisdiction.`,
      },
      {
        heading: "6. Intellectual property",
        body: `${PRODUCT} and all original content, features, and functionality remain the exclusive property of ${COMPANY}. You retain ownership of all candidate and job data you ingest.`,
      },
      {
        heading: "7. Termination",
        body: `We may suspend or terminate access for breach of these Terms. You may terminate at any time from the Settings page. Termination does not relieve you of any unpaid fees.`,
      },
      {
        heading: "8. Limitation of liability",
        body: `To the maximum extent permitted by law, ${COMPANY}'s aggregate liability is limited to fees paid in the 12 months preceding the claim. We are not liable for indirect or consequential damages.`,
      },
      {
        heading: "9. Governing law",
        body: `These Terms are governed by the laws of the State of Florida, USA, without regard to conflict-of-law principles. Any disputes will be resolved in the courts of Miami-Dade County.`,
      },
      {
        heading: "10. Changes",
        body: `We may update these Terms; we will notify you by email when material changes are made. Continued use of the Service after the effective date constitutes acceptance.`,
      },
    ],
  },
  disclaimer: {
    slug: "disclaimer",
    title: "Disclaimer",
    intro: `The information and AI outputs provided by ${PRODUCT} are for general informational purposes only. ${COMPANY} makes no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, or suitability of any information, AI score, or candidate ranking displayed on the platform.`,
    sections: [
      {
        heading: "1. Hiring decisions",
        body: `${PRODUCT} is a screening and scoring tool, not a hiring decision system. All employment decisions are made solely by the employer using the platform. ${COMPANY} disclaims liability for any hiring decision, including those that may be challenged under equal employment opportunity, anti-discrimination, or other employment laws.`,
      },
      {
        heading: "2. AI accuracy",
        body: `AI-generated transcripts, scores, summaries, and feedback may contain errors. Always verify critical information directly with the candidate before making decisions. AI output reflects patterns in training data and may not capture nuance, accommodations, or context.`,
      },
      {
        heading: "3. Third-party services",
        body: `${PRODUCT} integrates with third-party services (Stripe, Resend, Cloudflare, OpenAI, Anthropic). ${COMPANY} is not responsible for outages, errors, or data handling by these providers. Their use is governed by their own terms.`,
      },
      {
        heading: "4. No legal or compliance advice",
        body: `Nothing on ${PRODUCT} constitutes legal, employment law, or compliance advice. Consult qualified counsel before making decisions that may have regulatory consequences in your jurisdiction.`,
      },
      {
        heading: "5. External links",
        body: `Our site may link to third-party websites. ${COMPANY} has no control over and is not responsible for the content or practices of those sites.`,
      },
      {
        heading: "6. Contact",
        body: `Questions about this disclaimer? Email legal@hireloop.ai.`,
      },
    ],
  },
};

export const POLICY_META = { COMPANY, PRODUCT, EFFECTIVE };
